import Image from "next/image"
import { cn } from "@/lib/utils"

/**
 * BrowserFrame — wraps a product screenshot in a soft browser-window chrome
 * (traffic-light dots + faux address bar) sitting on a subtle gradient stage.
 * Gives marketing screenshots a polished, app-like feel without extra assets.
 */
export function BrowserFrame({
  src,
  alt,
  label,
  priority = false,
  className,
  imageClassName,
  sizes = "(max-width: 768px) 100vw, 50vw",
}: {
  src: string
  alt: string
  /** Optional pill shown in the faux address bar (e.g. "planami.app/planner"). */
  label?: string
  priority?: boolean
  className?: string
  imageClassName?: string
  sizes?: string
}) {
  return (
    <div
      className={cn(
        "group relative rounded-xl border border-border/60 bg-card shadow-xl shadow-primary/5 overflow-hidden transition-transform duration-500 ease-out hover:-translate-y-1",
        className,
      )}
    >
      {/* Window chrome */}
      <div className="flex items-center gap-2 px-3.5 py-2.5 border-b border-border/60 bg-muted/40">
        <span className="h-2.5 w-2.5 rounded-full bg-[#ff5f57]/80" />
        <span className="h-2.5 w-2.5 rounded-full bg-[#febc2e]/80" />
        <span className="h-2.5 w-2.5 rounded-full bg-[#28c840]/80" />
        {label && (
          <span className="ml-3 hidden sm:block truncate rounded-md bg-background/70 px-3 py-0.5 text-[11px] text-muted-foreground">
            {label}
          </span>
        )}
      </div>
      {/* Screenshot */}
      <div className="relative w-full aspect-[16/10] bg-background">
        <Image
          src={src}
          alt={alt}
          fill
          className={cn("object-contain", imageClassName)}
          loading={priority ? undefined : "lazy"}
          priority={priority}
          sizes={sizes}
        />
      </div>
    </div>
  )
}
